import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useField } from 'formik'
import { FiAlertCircle } from 'react-icons/fi'
import Tooltip from '../Tooltip'
import { IconBaseProps } from 'react-icons'

interface RadioGroupProps extends React.InputHTMLAttributes<HTMLInputElement> {
  name: string
  placeholder: string
  options: { label: string; value: string }[]
  sizeBox?: 'small' | 'large'
  icon?: React.ComponentType<IconBaseProps>
}

export function RadioGroup({
  name,
  placeholder,
  options,
  sizeBox = 'small',
  className,
  icon: Icon,
  ...rest
}: RadioGroupProps) {
  const [field, meta] = useField(name)

  const containerRef = useRef<HTMLDivElement>(null)

  const [isFocused, setIsFocused] = useState(false)
  const [isFilled, setIsFilled] = useState(false)

  const error = meta?.touched ? meta?.error : undefined
  const errorId = useMemo(() => `${name}-error`, [name])

  useEffect(() => {
    setIsFilled(!!field.value)
  }, [field.value])

  const focusFirstRadio = useCallback(() => {
    const first = containerRef.current?.querySelector(
      'input[type="radio"]',
    ) as HTMLInputElement | null
    first?.focus()
  }, [])

  const openAndFocus = useCallback(() => {
    setIsFocused(true)
    requestAnimationFrame(() => focusFirstRadio())
  }, [focusFirstRadio])

  const handleContainerMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!isFocused && !isFilled) {
        e.preventDefault()
        openAndFocus()
      }
    },
    [isFilled, isFocused, openAndFocus],
  )

  const handleContainerKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (isFocused || isFilled) return

      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        openAndFocus()
      }
    },
    [isFilled, isFocused, openAndFocus],
  )

  const handleFocus = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(true)
      rest.onFocus?.(e)
    },
    [rest],
  )

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      setTimeout(() => {
        const active = document.activeElement as HTMLElement | null
        const stillInside = !!(active && containerRef.current?.contains(active))
        setIsFocused(stillInside)

        if (!stillInside) {
          setIsFilled(!!field.value)
        }
      }, 0)

      field.onBlur(e)
      rest.onBlur?.(e)
    },
    [field, rest],
  )

  const isActive = isFocused || isFilled

  const borderColorClass = error
    ? 'border-[#C53030]'
    : isFocused
      ? 'border-[#8493F4]'
      : isFilled
        ? 'border-[rgba(132,147,244,0.6)]'
        : 'border-[rgba(174,174,174,0.6)]'

  const textColorClass = isFocused
    ? 'text-[#8493F4]'
    : isFilled
      ? 'text-[#D1D4E2]'
      : 'text-[#666360]'

  const paddingTopClass = isActive
    ? sizeBox === 'large'
      ? 'pt-[33px]'
      : 'pt-[24px]'
    : 'pt-[16px]'

  const containerClassName = [
    'relative flex w-full items-center rounded-[4px] border bg-transparent px-[12px] pb-[16px] mb-[12px] transition-[border-color,color,padding] duration-200',
    paddingTopClass,

    !isActive && sizeBox === 'small' ? 'h-[56px]' : '',
    sizeBox === 'large' ? 'h-[70px]' : '',

    borderColorClass,
    textColorClass,
    className,
  ]
    .filter(Boolean)
    .join(' ')

  const labelClassName = [
    'absolute left-[12px] top-[16px] pr-[12px] text-[12px] font-bold text-[#666360] transition-all duration-200 pointer-events-none',
    isActive ? '-translate-y-[12px] tracking-[0.1em]' : '',
    error && sizeBox === 'large' ? 'text-[13px] pr-[30px]' : '',
  ]
    .filter(Boolean)
    .join(' ')

  const optionIdPrefix = useMemo(() => `${name}-opt`, [name])

  return (
    <div
      ref={containerRef}
      className={containerClassName}
      onMouseDown={handleContainerMouseDown}
      onKeyDown={handleContainerKeyDown}
      role={!isActive ? 'button' : undefined}
      tabIndex={!isActive ? 0 : undefined}
      aria-label={!isActive ? placeholder : undefined}
      aria-expanded={!isActive ? false : undefined}
    >
      {Icon && (isActive || isFilled) && (
        <Icon
          size={20}
          className={`mr-[12px] ${
            isFilled ? 'text-[#8493F4]' : 'text-[#666360]'
          }`}
        />
      )}

      <div
        className={[
          'flex-1 flex justify-start transition-all duration-200 pl-4',
          isActive
            ? 'opacity-100 max-h-[200px] pointer-events-auto'
            : 'opacity-0 max-h-0 overflow-hidden pointer-events-none',
        ].join(' ')}
        role="radiogroup"
        aria-label={placeholder}
        aria-invalid={!!error}
        aria-describedby={error ? errorId : undefined}
      >
        <div className="flex flex-row flex-wrap gap-x-6 gap-y-3">
          {options.map((option, idx) => {
            const id = `${optionIdPrefix}-${idx}-${option.value}`

            return (
              <label
                key={option.value}
                htmlFor={id}
                className="flex items-center space-x-2 cursor-pointer select-none pt-2"
              >
                <input
                  id={id}
                  type="radio"
                  {...rest}
                  {...field}
                  value={option.value}
                  checked={field.value === option.value}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                  tabIndex={!isActive ? 0 : undefined}
                  className={[
                    'w-4 h-4',
                    'focus:ring-0 outline-none',
                    error
                      ? 'border-[#C53030]'
                      : 'border-[rgba(174,174,174,0.6)]',
                    'text-[#8493F4]',
                  ].join(' ')}
                />
                <span className="text-[#333]">{option.label}</span>
              </label>
            )
          })}
        </div>
      </div>

      {error && (
        <>
          <Tooltip
            title={String(error)}
            className="absolute right-[10px] ml-[12px] h-[20px] [&>svg]:m-0"
          >
            <FiAlertCircle color="#c53030" size={20} />
          </Tooltip>

          <span id={errorId} className="sr-only">
            {String(error)}
          </span>
        </>
      )}

      <label className={labelClassName}>{placeholder}</label>
    </div>
  )
}
